import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore } from '@mui/icons-material';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import PromoCodeForm from './PromoCodeForm';

const PromoCodesPanel = ({ characters = [] }) => {
  const { getToken } = useAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testOpen, setTestOpen] = useState(true);
  const [testPlayerId, setTestPlayerId] = useState('');
  const [testCode, setTestCode] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/promo/admin/list`, {
        headers: await getAuthHeaders(getToken)
      });
      if (!res.ok) throw new Error('Failed to load promo codes');
      const data = await res.json();
      setPromos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
      setPromos([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  const runRedeemTest = async () => {
    setTestResult(null);
    if (!testPlayerId.trim() || !testCode.trim()) {
      setTestResult({ clientError: 'Enter Player ID and Code' });
      return;
    }
    setTestLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/promo/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId: testPlayerId.trim(),
          code: testCode.trim().toUpperCase()
        })
      });
      const rawText = await res.text();
      let bodyJson = rawText;
      try {
        const parsed = JSON.parse(rawText);
        bodyJson = JSON.stringify(parsed, null, 2);
      } catch {
        /* keep rawText */
      }
      setTestResult({ httpStatus: res.status, body: bodyJson });
      if (res.ok) {
        fetchPromos();
      }
    } catch (e) {
      setTestResult({ clientError: e.message });
    } finally {
      setTestLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPromo(null);
    setFormOpen(true);
  };

  const handleEdit = (p) => {
    setEditingPromo(p);
    setFormOpen(true);
  };

  const handleSubmitForm = async (payload) => {
    try {
      if (editingPromo) {
        const res = await fetch(`${API_BASE_URL}/api/promo/admin/${editingPromo._id}`, {
          method: 'PUT',
          headers: {
            ...(await getAuthHeaders(getToken)),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            characterId: payload.characterId,
            relationshipBonus: payload.relationshipBonus,
            active: payload.active
          })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Update failed');
        }
      } else {
        const res = await fetch(`${API_BASE_URL}/api/promo/admin`, {
          method: 'POST',
          headers: {
            ...(await getAuthHeaders(getToken)),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Create failed');
        }
      }
      setFormOpen(false);
      setEditingPromo(null);
      fetchPromos();
    } catch (e) {
      setError(e.message);
    }
  };

  const confirmDelete = (p) => {
    setPromoToDelete(p);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!promoToDelete) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/promo/admin/${promoToDelete._id}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken)
      });
      if (!res.ok) throw new Error('Delete failed');
      setDeleteOpen(false);
      setPromoToDelete(null);
      fetchPromos();
    } catch (e) {
      setError(e.message);
    }
  };

  const charName = (p) => {
    const c = p.characterId;
    if (c && typeof c === 'object' && c.name) return c.name;
    return '—';
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
        Promo Codes
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Button
          startIcon={<ExpandMore sx={{ transform: testOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
          onClick={() => setTestOpen(!testOpen)}
          sx={{ mb: testOpen ? 2 : 0 }}
        >
          Test redeem (POST /api/promo/redeem)
        </Button>
        <Collapse in={testOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
            <TextField
              size="small"
              label="Player ID"
              value={testPlayerId}
              onChange={(e) => setTestPlayerId(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              label="Code"
              value={testCode}
              onChange={(e) => setTestCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              sx={{ minWidth: 160 }}
            />
            <Button variant="contained" onClick={runRedeemTest} disabled={testLoading}>
              {testLoading ? 'Calling…' : 'Redeem'}
            </Button>
          </Box>
          {testResult?.clientError && (
            <Alert severity="error">{testResult.clientError}</Alert>
          )}
          {testResult?.body != null && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                HTTP {testResult.httpStatus} — response body
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: 1,
                  borderColor: 'divider',
                  fontSize: '0.75rem',
                  fontFamily: 'ui-monospace, monospace',
                  overflow: 'auto',
                  maxHeight: 280,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {testResult.body}
              </Box>
            </Box>
          )}
        </Collapse>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Add Promo Code
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Character</TableCell>
                <TableCell width={100}>Bonus RP</TableCell>
                <TableCell width={100}>Redeemed</TableCell>
                <TableCell width={100}>Status</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 112 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No promo codes yet
                  </TableCell>
                </TableRow>
              ) : (
                promos.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.code}</TableCell>
                    <TableCell>{charName(p)}</TableCell>
                    <TableCell>{p.relationshipBonus}</TableCell>
                    <TableCell>{p.redeemedCount ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.active ? 'Active' : 'Inactive'}
                        color={p.active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 112 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.25
                        }}
                      >
                        <IconButton size="small" color="primary" onClick={() => handleEdit(p)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => confirmDelete(p)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PromoCodeForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingPromo(null);
        }}
        onSubmit={handleSubmitForm}
        promo={editingPromo}
        isEdit={!!editingPromo}
        characters={characters}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete promo code</DialogTitle>
        <DialogContent>
          <Typography>
            Remove &quot;{promoToDelete?.code}&quot;? Redemption history for this code will be deleted too.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoCodesPanel;
